import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Trash2, Scale } from 'lucide-react';
import { Container, PageHeader, SplitView, Breadcrumbs, BreadcrumbItem } from '@vnetwork/ui/layout';
import { StatusPill, type StatusTone } from '@vnetwork/ui/data';
import {
  Button,
  Badge,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Skeleton,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { useLoadBalancer, useDeleteLoadBalancer, useLoadBalancerMonitoring, type LoadBalancer } from '@/api/opennebula';
import { DescList, DescItem, monoValue } from '../../components/DescList';
import { ListenerManager } from './ListenerManager';
import { LbStats } from './LbStats';
import { LbIngress } from './LbIngress';
import { LoadBalancerResize } from './LoadBalancerResize';
import { MonitoringTab } from '../instances/MonitoringTab';

function tone(s?: string): StatusTone {
  const v = (s ?? '').toLowerCase();
  if (v.includes('on') || v.includes('active') || v.includes('run')) return 'success';
  if (v.includes('pend') || v.includes('deploy')) return 'warning';
  if (v.includes('error') || v.includes('fail') || v.includes('term')) return 'danger';
  return 'muted';
}

export function LoadBalancerDetail() {
  const { serviceId, lbId } = useParams<{ serviceId: string; lbId: string }>();
  const navigate = useNavigate();
  const { show } = useToast();
  const base = `/services/compute/${serviceId}`;
  const lbsPath = `${base}/load-balancers`;
  const { data: lb, isLoading } = useLoadBalancer(serviceId, lbId);
  const [del, setDel] = useState(false);
  const [resize, setResize] = useState(false);

  // Breadcrumb-rooted back path (parent crumb links to the list) — matches the
  // canonical compute detail pages; no floating "Back" button.
  const crumbs = (current: string) => (
    <Breadcrumbs>
      <BreadcrumbItem href={lbsPath} onClick={(e) => { e.preventDefault(); navigate(lbsPath); }}>Load Balancers</BreadcrumbItem>
      <BreadcrumbItem current>{current}</BreadcrumbItem>
    </Breadcrumbs>
  );

  const isK8s = !!lb?.metadata?.cluster?.id;
  // K8s-managed LBs are resized from the owning cluster's Ingress tab (the cluster
  // is the source of truth for the LB size), so deep-link there instead of a dead
  // disabled button. Older LBs may lack `service_uuid` — fall back to disabled.
  const k8sCluster = lb?.metadata?.cluster;
  const clusterIngressHref =
    k8sCluster?.service_uuid && k8sCluster?.id
      ? `/services/kubernetes/${k8sCluster.service_uuid}/clusters/${k8sCluster.id}/ingress`
      : undefined;

  const remove = useDeleteLoadBalancer(serviceId!, {
    onSuccess: () => { show({ tone: 'success', title: 'Load balancer terminating' }); navigate(`${base}/load-balancers`); },
    onError: () => show({ tone: 'danger', title: 'Delete failed' }),
  });

  if (isLoading || !lb) {
    return (
      <Container size="xl">
        <PageHeader breadcrumbs={crumbs('Load balancer')} icon={<Scale size={22} />} title={<Skeleton style={{ height: 24, width: 200 }} />} />
        <Skeleton style={{ height: 120 }} />
      </Container>
    );
  }

  const publicIp = lb.metadata?.nics?.find((n) => /public/i.test(n.network ?? ''))?.ip;

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={crumbs(lb.name || lb.id)}
        icon={<Scale size={22} />}
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
            {lb.name || lb.id}
            <StatusPill tone={tone(lb.status || lb.state)}>{lb.status || lb.state || '—'}</StatusPill>
            {isK8s && <Badge tone="info" appearance="subtle" size="sm">Kubernetes-managed</Badge>}
          </span>
        }
        actions={
          <>
            {isK8s ? (
              <Button
                size="sm"
                variant="secondary"
                startIcon={<Scale size={15} />}
                disabled={!clusterIngressHref}
                title={
                  clusterIngressHref
                    ? `Managed by cluster ${k8sCluster?.name ?? ''} — resize from its Ingress tab`
                    : 'Managed by Kubernetes — resize from the cluster Ingress tab'
                }
                onClick={() => clusterIngressHref && navigate(clusterIngressHref)}
              >
                Resize in cluster
              </Button>
            ) : (
              <Button
                size="sm"
                variant="secondary"
                startIcon={<Scale size={15} />}
                disabled={(lb.status || '').toUpperCase() === 'TERMINATED'}
                onClick={() => setResize(true)}
              >
                Resize
              </Button>
            )}
            <Button size="sm" variant="danger" startIcon={<Trash2 size={15} />} disabled={isK8s} onClick={() => setDel(true)}>Delete</Button>
          </>
        }
      />
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="listeners">Listeners</TabsTrigger>
          <TabsTrigger value="monitoring">Monitoring</TabsTrigger>
          <TabsTrigger value="stats">Statistics</TabsTrigger>
          <TabsTrigger value="ingress">Ingress</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <LbOverview lb={lb} publicIp={publicIp} />
        </TabsContent>
        <TabsContent value="listeners">
          {isK8s ? (
            <LbIngress serviceId={serviceId!} lb={lb} />
          ) : (
            <ListenerManager serviceId={serviceId!} lb={lb} />
          )}
        </TabsContent>
        <TabsContent value="monitoring">
          <MonitoringTab
            serviceId={serviceId!}
            instance={lb}
            useMonitoring={useLoadBalancerMonitoring}
            description="CPU, memory, disk and network for this load balancer."
          />
        </TabsContent>
        <TabsContent value="stats">
          <LbStats serviceId={serviceId!} lbId={lb.id} />
        </TabsContent>
        <TabsContent value="ingress">
          <LbIngress serviceId={serviceId!} lb={lb} />
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={del}
        onOpenChange={setDel}
        title="Delete load balancer?"
        description={`Terminate “${lb.name || lb.id}”? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        requireText={lb.name || lb.id}
        loading={remove.isPending}
        onConfirm={() => remove.mutate(lb.id)}
      />

      {resize && <LoadBalancerResize serviceId={serviceId!} lb={lb} onClose={() => setResize(false)} />}
    </Container>
  );
}

function LbOverview({ lb, publicIp }: { lb: LoadBalancer; publicIp?: string }) {
  const m = lb.metadata;
  const r = m?.resources;
  const listeners = m?.listeners?.length ?? 0;

  // Rail: how you reach the load balancer (public endpoint) kept separate from
  // the identity card, mirroring the instance overview's connection panel.
  const rail = (
    <Card flat>
      <CardHeader><CardTitle style={{ fontSize: 'var(--vn-font-size-md)' }}>Connection</CardTitle></CardHeader>
      <CardBody>
        <DescList minColumn={999} style={{ gap: 'var(--vn-space-3)' }}>
          <DescItem label="Public IP">{publicIp ? <code style={monoValue}>{publicIp}</code> : '—'}</DescItem>
          <DescItem label="Router">{m?.router?.name || '—'}</DescItem>
        </DescList>
      </CardBody>
    </Card>
  );

  return (
    <SplitView asideWidth="300px" aside={rail}>
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {/* Identity — what the load balancer is. Status lives in the page header
          pill; Public IP / Router in the Connection rail — no duplicate KPI row. */}
      <Card>
        <CardHeader><CardTitle>Load balancer information</CardTitle></CardHeader>
        <CardBody>
          <DescList>
            <DescItem label="Name">{m?.name || lb.name || '—'}</DescItem>
            <DescItem label="Type">{m?.type || '—'}</DescItem>
            <DescItem label="Image">{m?.image?.name || '—'}</DescItem>
            <DescItem label="Resources">{r ? `${r.cores ?? '?'} vCPU · ${r.memory ?? '?'} GB · ${r.volumes ?? '?'} GB disk` : '—'}</DescItem>
            <DescItem label="Router">{m?.router?.name || '—'}</DescItem>
            <DescItem label="Listeners">{listeners}</DescItem>
            <DescItem label="ID"><code style={monoValue}>{lb.id}</code></DescItem>
          </DescList>
        </CardBody>
      </Card>

      {(m?.nics?.length ?? 0) > 0 && (
        <Card>
          <CardHeader><CardTitle>Network interfaces</CardTitle></CardHeader>
          <CardBody>
            <div style={{ display: 'grid', gap: 6 }}>
              {m!.nics!.map((n, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--vn-font-size-sm)', padding: '4px 0', borderTop: i ? '1px solid var(--vn-border-default)' : 'none' }}>
                  <span>{n.name || `NIC${i}`} · {n.network}</span>
                  <code style={monoValue}>{n.ip}</code>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}
      </div>
    </SplitView>
  );
}
